import React from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

// Heart SVG path data — shared across mask URI and fallback background URIs.
// Tight-cropped viewBox: x 6..30 (24w), y 5..25.5666 (~20.5666h).
const HEART_SVG_OPEN =
  'data:image/svg+xml,%3Csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20viewBox=%276%205%2024%2020.5666%27%3E%3Cpath%20';
const HEART_SVG_CLOSE =
  'd=%27M30%2011.6429C30%2019.1429%2018.8796%2025.2136%2018.4061%2025.4643C18.2813%2025.5314%2018.1417%2025.5666%2018%2025.5666C17.8583%2025.5666%2017.7187%2025.5314%2017.5939%2025.4643C17.1204%2025.2136%206%2019.1429%206%2011.6429C6.00199%209.88167%206.70249%208.19319%207.94784%206.94784C9.19319%205.70249%2010.8817%205.00199%2012.6429%205C14.8554%205%2016.7925%205.95143%2018%207.55964C19.2075%205.95143%2021.1446%205%2023.3571%205C25.1183%205.00199%2026.8068%205.70249%2028.0522%206.94784C29.2975%208.19319%2029.998%209.88167%2030%2011.6429Z%27/%3E%3C/svg%3E';

// No fill attr — used as mask shape
const HEART_MASK_URI = `${HEART_SVG_OPEN}${HEART_SVG_CLOSE}`;
// Baked-in fill colors — used as background-image fallback when mask is unsupported
const HEART_WHITE_URI = `${HEART_SVG_OPEN}fill=%27rgba(255,255,255,0.9)%27%20${HEART_SVG_CLOSE}`;
const HEART_DARK_URI = `${HEART_SVG_OPEN}fill=%27rgba(27,28,25,0.8)%27%20${HEART_SVG_CLOSE}`;

// sizes resized to match mocks
const HEART_W = 24;
const HEART_H = 20.5;

const Wrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
`;

const IconSurface = styled.span`
  position: relative;
  width: 32px;
  height: 32px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border-radius: 999px;
  filter: drop-shadow(0 0 6px rgba(0, 0, 0, 0.2));

  pointer-events: none;
`;

const Heart = styled.span`
  width: ${HEART_W}px;
  height: ${HEART_H}px;
  /* Fallback: heart SVG with baked-in fill as background-image.
     Renders a heart shape on browsers that lack mask-image support. */
  background: transparent;
  background-image: url('${({ $selected }) =>
    $selected ? HEART_DARK_URI : HEART_WHITE_URI}');
  background-size: 100% 100%;
  background-repeat: no-repeat;

  /* Upgrade: when mask is supported, use solid/gradient background + mask
     for the smooth background transition on select/deselect. */
  @supports (-webkit-mask-image: url('')) or (mask-image: url('')) {
    background-image: none;
    transition: background 180ms ease;

    ${({ $selected, theme }) =>
      $selected
        ? css`
            background: linear-gradient(
              127deg,
              ${theme.colors.iconColorWishlistOverlayFilled} 0%,
              ${theme.colors.iconColorWishlistOverlayFilledEnd} 100.9%
            );
          `
        : css`
            background: ${theme.colors.iconColorWishlistOverlayEmpty};
          `}

    -webkit-mask-image: url("${HEART_MASK_URI}");
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
    -webkit-mask-size: 100% 100%;

    mask-image: url('${HEART_MASK_URI}');
    mask-repeat: no-repeat;
    mask-position: center;
    mask-size: 100% 100%;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export default function HeartIcon({ className, isSelected = false }) {
  return (
    <Wrapper className={className}>
      <IconSurface>
        <Heart $selected={isSelected} />
      </IconSurface>
    </Wrapper>
  );
}

HeartIcon.propTypes = {
  className: PropTypes.string,
  isSelected: PropTypes.bool,
};
